import { Stack, Typography } from '@mui/material';
import ChipLabel, { type CHIP_COLORS } from '/@/components/chip-label';
import { _ } from '/@/locale';
import type { FieldRisk } from './use-field-risks';

/**
 * Tolerance bands over the metric scale. Metric values run 1..metricsMaxValue where a LOW
 * value means tolerant, and the existing dots reference splits that range into three labelled
 * thirds (Tolerante / Moderado / Susceptible). We reuse the same cuts so a hybrid never reads
 * as tolerant here and susceptible on the datasheet.
 */
export type Tolerance = 'tolerant' | 'moderate' | 'susceptible';

export const getTolerance = (value: number, maxValue: number): Tolerance => {
    const band = (maxValue || 9) / 3;
    if (value <= band) return 'tolerant';
    if (value <= band * 2) return 'moderate';
    return 'susceptible';
};

const TOLERANCE_STYLES: Record<Tolerance, { label: () => string; type: keyof typeof CHIP_COLORS }> = {
    tolerant: { label: () => _('Tolera'), type: 'success' },
    moderate: { label: () => _('Moderado'), type: 'warning' },
    susceptible: { label: () => _('No tolera'), type: 'danger' },
};

export type MetricValue = { id: number; value: number };

/** Resolves each of the field's risk diseases against this hybrid's metric scores. */
export const getDefensiveProfile = (risks: FieldRisk[], metrics: MetricValue[] | undefined, maxValue: number) =>
    risks
        .map((risk) => {
            const metric = (metrics || []).find(({ id }) => id === risk.cornMetricId);
            if (metric?.value === undefined || metric?.value === null) return undefined;
            return { name: risk.name, tolerance: getTolerance(metric.value, maxValue) };
        })
        .filter((row): row is { name: string; tolerance: Tolerance } => row !== undefined);

export function ToleranceBadge({ tolerance }: { tolerance: Tolerance }) {
    const style = TOLERANCE_STYLES[tolerance];
    return <ChipLabel label={style.label()} type={style.type} size='sm' />;
}

/**
 * "Perfil defensivo" block of the hybrid card. Desktop lists one disease per row with the
 * badge pushed right; mobile inlines "disease: tolerance" as wrapping chips.
 */
function DefensiveProfile({ rows, compact }: { rows: { name: string; tolerance: Tolerance }[]; compact?: boolean }) {
    if (!rows.length) return null;

    if (compact) {
        return (
            <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                {rows.map((row) => {
                    const style = TOLERANCE_STYLES[row.tolerance];
                    return (
                        <ChipLabel key={row.name} label={`${row.name}: ${style.label()}`} type={style.type} size='xs' />
                    );
                })}
            </Stack>
        );
    }

    return (
        <Stack spacing={1}>
            <Typography variant='overline' color='primary' textTransform='uppercase'>
                {_('Perfil defensivo')}
            </Typography>
            <Stack spacing={0.5}>
                {rows.map((row) => (
                    <Stack key={row.name} direction='row' alignItems='center' justifyContent='space-between' gap={1}>
                        <Typography variant='body2' color='primary'>
                            {row.name}
                        </Typography>
                        <ToleranceBadge tolerance={row.tolerance} />
                    </Stack>
                ))}
            </Stack>
            <Typography variant='caption' color='primary.mui.inactive'>
                {_('Comportamiento según las incidencias principales del lote')}
            </Typography>
        </Stack>
    );
}

export default DefensiveProfile;
